import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Card, Loader, Typography } from '@altermaind/web/ui';
import { Form } from '@/_shared/components/Form';
import { Routes } from '@/_shared/constants/routes';
import { getConfirmTokenCall, postAcceptInvitationCall } from '@/invitation/state';
import { ErrorFeedback, SuccessFeedback, TokenExpiredFeedback } from '../components';

type FormValues = {
  password: string;
  passwordConfirm: string;
};

const MIN_PASSWORD_LENGTH = 8;

export const SetPasswordScreen: FC = () => {
  const t = useTranslations('invitation.setPassword');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { control, watch } = useForm<FormValues>({
    defaultValues: { password: '', passwordConfirm: '' },
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmTouched, setConfirmTouched] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [passwordBlurred, setPasswordBlurred] = useState(false);

  const {
    loading: loadingGetConfirmToken,
    error: errorGetConfirmToken,
    success: successGetConfirmToken,
    isTokenExpired,
    execute: executeGetConfirmToken,
  } = getConfirmTokenCall.hook();

  const {
    loading: loadingPostAcceptInvitation,
    success: successPostAcceptInvitation,
    error: errorPostAcceptInvitation,
    execute: executePostAcceptInvitation,
  } = postAcceptInvitationCall.hook();

  const password = watch('password') ?? '';
  const passwordConfirm = watch('passwordConfirm') ?? '';

  const rules = useMemo(
    () => [
      { key: 'length', label: t('rules.length'), isValid: password.length >= MIN_PASSWORD_LENGTH },
      { key: 'upper', label: t('rules.upper'), isValid: /[A-Z]/.test(password) },
      { key: 'lower', label: t('rules.lower'), isValid: /[a-z]/.test(password) },
      { key: 'digit', label: t('rules.digit'), isValid: /\d/.test(password) },
    ],
    [password, t],
  );

  const allRulesValid = rules.every(r => r.isValid);
  const passwordsMatch = password === passwordConfirm && passwordConfirm !== '';
  const showMismatch = confirmTouched && passwordConfirm !== '' && password !== passwordConfirm;
  const canSubmit = allRulesValid && passwordsMatch;
  const showPasswordError = passwordBlurred && !allRulesValid && !passwordFocused;

  const token = searchParams.get('token');

  const onConfirm = useCallback(() => {
    if (token) {
      executePostAcceptInvitation({
        token,
        password,
      });
    }
  }, [executePostAcceptInvitation, password, token]);

  useEffect(() => {
    if (!token) {
      navigate(Routes.Login, { replace: true });
    } else if (!successGetConfirmToken && !errorGetConfirmToken && !loadingGetConfirmToken) {
      executeGetConfirmToken({ token });
    }
  }, [
    errorGetConfirmToken,
    executeGetConfirmToken,
    loadingGetConfirmToken,
    navigate,
    successGetConfirmToken,
    token,
  ]);

  if (loadingGetConfirmToken) {
    return (
      <Box display='flex' justifyContent='center' alignItems='center' minHeight='100vh'>
        <Loader size='xl' />
      </Box>
    );
  }

  if (errorGetConfirmToken) {
    if (isTokenExpired) {
      return <TokenExpiredFeedback namespace='invitation.setPassword.tokenExpired' />;
    }
    return <ErrorFeedback namespace='invitation.setPassword.error' />;
  }

  if (successPostAcceptInvitation) {
    return <SuccessFeedback namespace='invitation.setPassword.success' />;
  }

  if (errorPostAcceptInvitation) {
    return <ErrorFeedback namespace='invitation.setPassword.error' />;
  }

  if (successGetConfirmToken) {
    return (
      <Box
        display='flex'
        justifyContent='center'
        alignItems='center'
        minHeight='100vh'
        backgroundColor='primary/background'
        px='m'
      >
        <Box width='100%' style={{ maxWidth: 600 }}>
          <Typography variant='special' textAlign='center' mb='xxxs'>
            {t('title')}
          </Typography>
          <Typography variant='p2/light' textAlign='center' mb='xs'>
            {t('subTitle')}
          </Typography>

          <Card backgroundColor='transparent'>
            {/* New password */}
            <Box mb='xxs' style={{ position: 'relative' }}>
              <Form.FormInput
                // @ts-ignore
                control={control}
                name='password'
                label={t('fields.password')}
                type={showPassword ? 'text' : 'password'}
                autoComplete='new-password'
                hasError={showPasswordError}
                containerProps={{
                  onFocus: () => setPasswordFocused(true),
                  onBlur: () => {
                    setPasswordFocused(false);
                    setPasswordBlurred(true);
                  },
                }}
                suffixSvg={showPassword ? 'eyeOn' : 'eyeOff'}
                onSuffixPress={() => setShowPassword(v => !v)}
              />
            </Box>

            {/* Password rules */}
            <Box mb='s' mt='xxs'>
              {rules.map(rule => (
                <Box key={rule.key} display='flex' alignItems='center' mt='xxxs'>
                  <Typography variant='p2/light' color={rule.isValid ? 'primary' : 'grey/light'}>
                    {rule.label}
                  </Typography>
                  {rule.isValid && (
                    <Typography variant='p2/light' color='primary' ml='xxs'>
                      ✓
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            {/* Confirm password */}
            <Box mb='m' style={{ position: 'relative' }}>
              <Form.FormInput
                // @ts-ignore
                control={control}
                name='passwordConfirm'
                label={t('fields.passwordConfirm')}
                type={showConfirm ? 'text' : 'password'}
                hasError={showMismatch}
                containerProps={{
                  onBlur: () => setConfirmTouched(true),
                }}
                suffixSvg={showConfirm ? 'eyeOn' : 'eyeOff'}
                onSuffixPress={() => setShowConfirm(v => !v)}
              />
              {showMismatch && (
                <Typography variant='p3/light' color='error' mt='xxxs'>
                  {t('errors.mismatch')}
                </Typography>
              )}
            </Box>

            <Box display='flex' justifyContent='center' mt='xs'>
              <Button
                variant='primary'
                disabled={!canSubmit || loadingPostAcceptInvitation}
                loading={loadingPostAcceptInvitation}
                spinnerColor='white'
                style={{ minWidth: 200 }}
                onClick={onConfirm}
              >
                {t('buttons.submit')}
              </Button>
            </Box>
          </Card>
        </Box>
      </Box>
    );
  }

  return null;
};
